import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';
import jwtEncode from 'jwt-encode';

import type { IStates } from '../interface/IReducer';
import type { ILogin, IUserState } from '../interface/state/IAuthState';

export const USERDATACOOKIE = '_ud1234567891EL0';
export const DRAFTCOOKIE = '_udraft1234567891EL0';

// Nigerian bank account numbers (NUBAN) are always exactly 10 digits.
export const ACCOUNT_NUMBER_LENGTH = 10;
export const ACCOUNT_NUMBER_REGEX = /^\d{10}$/;
export const ACCOUNT_NUMBER_ERROR = 'Account number must be exactly 10 digits.';
export const sanitizeAccountNumber = (value: string) => value.replace(/\D/g, '').slice(0, ACCOUNT_NUMBER_LENGTH);

export const onLogout = () => {
	Cookies.remove(USERDATACOOKIE);
	localStorage.clear();
};

export interface IToken {
	UserId: string;
	FullName: string;
	Email: string;
	role: string;
	nbf: number;
	exp: number;
	iat: number;
	iss: string;
	aud: string;
}

interface IResponseToken {
	token: any;
	refreshToken: any;
	sub: string;
	name: string;
	iat: number;
}

export const encodeData = (token: any, refreshToken: any) => {
	try {
		const jst = jwtEncode(
			{
				sub: '1516239022',
				name: 'ERMS',
				iat: Math.floor(Date.now() / 1000), // Use a valid timestamp
				token,
				refreshToken,
			},
			process.env.REACT_APP_COOKIESECRET
			// { expiresIn: '1h' } // Change 'expires' to 'expiresIn'
		);
		Cookies.set(USERDATACOOKIE, jst);
	} catch (error) {
		console.log(error);
	}
};

// function roughSizeOfObject(object: any): number {
//   const objectList = new Set<object>() // To track visited objects
//   const stack = [object] // Stack for traversing properties
//   let bytes = 0 // Accumulator for the total size in bytes

//   while (stack.length) {
//     const value = stack.pop()

//     if (typeof value === 'boolean') {
//       bytes += 4 // Booleans are approximately 4 bytes
//     } else if (typeof value === 'string') {
//       bytes += value.length * 2 // Strings are roughly 2 bytes per character
//     } else if (typeof value === 'number') {
//       bytes += 8 // Numbers are typically 8 bytes (64-bit floating point)
//     } else if (typeof value === 'object' && value !== null) {
//       if (objectList.has(value)) {
//         continue // Skip objects that have already been processed
//       }
//       objectList.add(value)

//       // Push each property of the object onto the stack
//       for (const key in value) {
//         if (Object.prototype.hasOwnProperty.call(value, key)) {
//           stack.push(value[key])
//         }
//       }
//     }
//   }
//   return bytes // Return the estimated size in bytes
// }

export const encodeDraft = (data: any, saveDraft: (data: string) => void) => {
	try {
		const jst = jwtEncode(
			{
				sub: '151623902266',
				name: 'DRAFT',
				iat: Math.floor(Date.now() / 1000), // Use a valid timestamp
				data,
			},
			'secret'
		);
		// console.log(jst, roughSizeOfObject(data), 'juju')
		saveDraft(jst);
	} catch (error) {
		console.log('error');
	}
};

const getUser = (): IResponseToken => {
	try {
		const data = Cookies.get(USERDATACOOKIE);
		if (!data) throw new Error('data not valid');
		const userData: IResponseToken = jwtDecode(data);
		if (!userData) throw new Error('user not logged in');
		return userData;
	} catch (e) {
		return {} as IResponseToken;
	}
};

export const getUserData = (): ILogin => {
	try {
		return {
			user: getUser().token ? jwtDecode(getUser().token) : ({} as IUserState),
			token: getUser().token,
			refreshToken: getUser().refreshToken,
		};
	} catch (e) {
		return {} as ILogin;
	}
};

export const PRIMARY_COLOR = '#286439';
export const PRIMARY_COLOR_LIGHT = 'rgb(218, 239, 224)';

export const ISSESSION = () => {
	const timestamp = getUserData()?.user?.exp || 0;
	if (!timestamp) return false;
	const currentTimestamp = Math.floor(Date.now() / 1000);
	return timestamp > currentTimestamp;
};

export const ISADMINLOGGED = () => !!getUserData().user?.UserId;

// The JWT `role` claim is a string for a single role and an array for several.
export const HASROLE = (role: string) => {
	const claim: string | string[] | undefined = getUserData().user?.role;
	return Array.isArray(claim) ? claim.includes(role) : claim === role;
};

export const ISPARTYAGENT = () => HASROLE('Party Agent');

// Independent (field) observers: neutral, scoped to their accreditation organisation, never to a
// party. They report incidents, keep an election-day checklist and talk to their organisation's
// desk — no result upload or party analytics. An account holding both roles is treated as a Party Agent.
export const ISOBSERVER = () => HASROLE('Observer') && !ISPARTYAGENT();

// An observer organisation's admin. They work in the admin app, not here.
export const ISOBSERVERADMIN = () => HASROLE('Observer Admin');

/** The exact core role name of an Independent Supporter account (api.core SupporterAccessCatalogue). */
export const SUPPORTER_ROLE = 'Independent Supporter';

// Independent Supporters: members of the public a Super Admin links to one candidate and some of
// their elections. They follow the party's result coverage and report incidents of their own — no
// result upload, assignments, KYC, onboarding, posting, location tracking or chat. An account that
// also holds Party Agent or Observer is treated as that role instead.
export const ISSUPPORTER = () => HASROLE(SUPPORTER_ROLE) && !ISPARTYAGENT() && !HASROLE('Observer');

/** Roles that may use the agent app. Exact names: "Observer Admin" must not pass as "Observer". */
export const AGENT_APP_ROLES = ['Party Agent', 'Observer', SUPPORTER_ROLE] as const;

// export const COREAPI = 'https://core-ielect.ngrok.app';
// export const COMMANDAPI = 'https://command-ielect.ngrok.app';
// export const QUERYAPI = 'https://query-ielect.ngrok.app';
// export const EXTRACTAPI = 'https://erms-ocr.ngrok.app';
export const COREAPI = process.env.REACT_APP_BASE_CORE;
export const COMMANDAPI = process.env.REACT_APP_BASE_COMMAND;
// export const COMMANDAPI = 'https://e464fe81f852.ngrok-free.app';
// export const QUERYAPI = 'https://f6a4f0f4da85.ngrok-free.app';
export const QUERYAPI = process.env.REACT_APP_BASE_QUERY;
export const EXTRACTAPI = process.env.REACT_APP_BASE_EXTRACT;
export const KYCAPI = process.env.REACT_APP_BASE_KYC;
export const FEEDBACKAPI = process.env.REACT_APP_BASE_FEEDBACK;
export const MEDIAMANAGERAPI = process.env.REACT_APP_BASE_MEDIA_MANAGER;
// VESR (Virtual Election Situation Room) — own subdomain, NOT the gateway.
// The agent's core JWT is sent directly as Bearer; VESR validates it (shared
// signing key) and normalizes claims server-side. No token exchange.
export const VESRAPI = process.env.REACT_APP_BASE_VESR?.replace(/\/+$/, '');
export const SIGNALR_VESR = `${VESRAPI}/hubs/vesr`;

export const PAGE_SIZE = 20;

export interface IStateData {
	states?: IStates;
}

export const resultTypeEnum = {
	EC8A: 'Statement of Result at the Polling Unit',
	EC8B: 'Summary of Results at the Ward Collation Level',
	EC8C: 'Summary of Results at the Local Government Area (LGA) Collation Level',
	EC8D: 'Summary of Results at the State Collation Level',
	EC8E: 'Summary of Results at the National Collation Level',
	EC4OH: 'List of Accredited Voters',
	EC4OG: 'Ballot Paper Accounting and Verification Statement',
	EC4OA: 'Polling Unit Accounting Form',
	EC4OB: 'Polling Unit Material Receipt Form',
	EC4OC: 'Polling Unit Staff Oath/Affirmation of Neutrality Form',
} as const;

export type ResultType = keyof typeof resultTypeEnum;

/**
 * Coerce a raw level string to a canonical `ResultType`, or null when it names no known level.
 *
 * The API returns the level in lower case — `IElectionOfficialByQuery.assignment.resultType` is
 * typed `Lowercase<ResultType>` — while every consumer compares against the upper-case keys above.
 * Call sites have papered over that with `as ResultType`, which is invisible to `tsc` and produced
 * a panel that spun forever with no request and no error. Normalise here instead of casting.
 *
 * Returning null rather than a blind cast is deliberate: it lets a caller *say* the level is
 * unsupported instead of silently doing nothing.
 */
export const toResultType = (value?: string | null): ResultType | null => {
	const key = (value || '').trim().toUpperCase();
	return key in resultTypeEnum ? (key as ResultType) : null;
};

export const minStyle = (min?: boolean) => ({
	fontSize: min ? '12px' : '',
	height: min ? '40px' : '',
	border: min ? '1px solid #d1d1d1' : '',
});

export const ISDEVELOPMENT = true;
